import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styles from './PortalCombobox.module.scss';
import inputStyles from './FormField.module.scss';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface PortalComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function PortalCombobox({ options, value, onChange, placeholder }: PortalComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  // Coordinates of the rendered dropdown. Recomputed when opened or when an
  // ancestor scrolls/resizes, so the portaled dropdown tracks the input.
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  const filtered = useMemo(() => {
    if (!query) {
      return options;
    }
    const lower = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, query]);

  useEffect(() => {
    setHighlightIndex(-1);
  }, [filtered]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideDropdown = listRef.current?.contains(target);
      if (!insideContainer && !insideDropdown) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position the portaled dropdown beneath the input. Capture-phase scroll
  // listener catches scrolls on any ancestor (e.g. a modal body), so the
  // dropdown follows the input rather than detaching.
  useLayoutEffect(() => {
    if (!open || !containerRef.current) {
      return;
    }
    function updateCoords() {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [open]);

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex] as HTMLElement | undefined;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightIndex]);

  function handleSelect(optionValue: string) {
    onChange(optionValue);
    setQuery('');
    setOpen(false);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setQuery(e.target.value);
    if (!open) {
      setOpen(true);
    }
  }

  function handleFocus() {
    setOpen(true);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < filtered.length) {
        handleSelect(filtered[highlightIndex].value);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const dropdown =
    open && filtered.length > 0 && coords
      ? ReactDOM.createPortal(
          <ul
            className={styles.dropdown}
            ref={listRef}
            role="listbox"
            style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width }}
          >
            {filtered.map((option, i) => (
              <li
                key={option.value}
                className={[
                  styles.option,
                  i === highlightIndex ? styles.highlighted : '',
                  option.value === value ? styles.selected : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseDown={() => handleSelect(option.value)}
                onMouseEnter={() => setHighlightIndex(i)}
                role="option"
                aria-selected={option.value === value}
              >
                {option.label}
              </li>
            ))}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div className={styles.combobox} ref={containerRef}>
      <input
        className={inputStyles.portalInput}
        value={open ? query : selectedLabel}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      {dropdown}
    </div>
  );
}
